import './playlist-item-form.scss';
import {createRef, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {PlaylistItem} from '../../backend/interfaces/playlist-item';
import {useOffice} from '../../contexts/office/context';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {ContentPage} from '../content-page/content-page';
import {LoadingContent} from '../loading-content/loading-content';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Divider,
  Fab,
  FormLabel,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {translations} from '../../i18n/translations';
import {SubmitButton, SubmitButtonState} from '../submit-button/submit-button';
import {LocalizedStringInput} from '../localized-string-input/localized-string-input';
import {LocalizedString} from '../../backend/interfaces/localized-string';
import {MultipleSelectInput} from '../multiple-select-input/multiple-select-input';
import {regions} from '../../utils/data/regions';
import {SelectInput} from '../select-input/select-input';
import {
  FlatSearchSelectInput,
  SearchSelectOption,
} from '../flat-search-select-input/flat-search-select-input';
import {PlaylistsRepository} from '../../repositories/playlists';
import {TextInput} from '../text-input/text-input';
import {appSizes} from '../../utils/constants/sizes';
import {ImageCropperDialog} from '../image-cropper-dialog/image-cropper-dialog';
import {SmoothImage} from '../smooth-image/smooth-image';
import {RouterPrompt} from '../router-prompt/router-prompt';
import {URL_REGEX} from '../../utils/constants/regex';
import {PostPlaylistItem} from '../../backend/requests/post-playlist-item';
import {
  nullifyLocalizedString,
  sanitizeLocalizedString,
} from '../../utils/functions/sanitize-localized-string';
import {ScreenPaths} from '../../enums/screen-path';
import {StorageType} from '../../backend/enums/storage-type';
import {
  PlaylistItemThumbnailStorageRequestData,
  StorageRequestResult,
} from '../../backend/interfaces/storage-request';
import {StorageFileType} from '../../backend/enums/storage-file-type';
import {PostStorageRequest} from '../../backend/requests/post-storage-request';
import {StorageRequestRepository} from '../../repositories/storage-request';
import {PutPlaylistItem} from '../../backend/requests/put-playlist-item';
import {AppLanguageTag} from '../../i18n/types';
import {avatarSizesAndCoefficients} from '../../utils/constants/avatar-sizes-and-coefficients';
import {EmbedYoutubeVideo} from '../embed-youtube-video/embed-youtube-video';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import i18n from 'i18next';
import {CaregiverMode} from '../../enums';
import {ReadOnlyTextField} from '../read-only-text-field/read-only-text-field';
import {Modal} from '../modal/modal';

interface SearchSelectInputItem {
  id: string;
  name: string;
  picture?: string;
}

/**
 * Form to view, edit, remove, and create playlist items.
 *
 * It uses the office context to get the playlist item's information.
 *
 * Permissions are given by props.
 */
export const PlaylistItemForm = ({
  caregiverMode,
  canWrite,
  ...props
}: {
  caregiverMode: CaregiverMode;
  canWrite: boolean;
  showPageTitle: boolean;
  showExitButton: boolean;
  redirectUrl: string;
  entityId?: string;
}) => {
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  let history = useHistory();
  const {t} = useTranslation();
  const theme = useTheme();

  // Hold state
  const [originalModel, setOriginalModel] = useState<PlaylistItem | undefined>();
  const [model, setModel] = useState<PlaylistItem | undefined>();
  const [thumbnailImageFile, setThumbnailImageFile] = useState<File | undefined>();
  const [thumbnailImageCropperFile, setThumbnailImageCropperFile] = useState<File | undefined>();
  const [playlist, setPlaylist] = useState<SearchSelectInputItem | undefined>();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState<boolean>(false);

  // Form validation
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [titleError, setTitleError] = useState<string | undefined>();
  const [showTitleError, setShowTitleError] = useState<boolean>(false);
  const [playlistError, setPlaylistError] = useState<string | undefined>();
  const [showPlaylistError, setShowPlaylistError] = useState<boolean>(false);
  const [resourceUrlError, setResourceUrlError] = useState<string | undefined>();
  const [showResourceUrlError, setShowResourceUrlError] = useState<boolean>(false);

  // Dialogs
  const [deleteDialogOpened, setDeleteDialogOpened] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Ref
  const thumbnailImageInputFileRef = createRef<any>();

  // Set model when we get one
  useEffect(() => {
    // Set the model
    const init = async () => {
      if (officeContext.state.playlistItemForm) {
        // Init buttons state
        officeContext.setPlaylistItemFormSubmitButtonState(SubmitButtonState.Normal);
        officeContext.setPlaylistItemFormDeleteButtonState(SubmitButtonState.Normal);

        // Init some state
        setDataChanged(false);
        setThumbnailImageFile(undefined);

        // Get the playlist
        if (officeContext.state.playlistItemForm.playlistId) {
          if (caregiverMode === CaregiverMode.SystemAdministrator) {
            try {
              // Get playlist
              const result =
                await PlaylistsRepository.instance.getPlaylistByIdAsSystemAdministrator(
                  officeContext.state.playlistItemForm.playlistId,
                  true
                );

              if (result) {
                setPlaylist({
                  id: result.id || '',
                  name: result.name,
                  picture: result.imageUrl,
                });
              } else {
                // Error
                throw new Error('Empty playlist.');
              }
            } catch (error) {
              // Log
              console.debug('🔴 Could not get the playlist.', error);
            }
          } else if (caregiverMode === CaregiverMode.Professional) {
            try {
              // Check id
              if (!props.entityId) {
                throw new Error('Empty entity id.');
              }

              // Get playlist
              const result =
                await PlaylistsRepository.instance.getPlaylistByIdForEntityAsAffiliatedForEntity(
                  props.entityId,
                  officeContext.state.playlistItemForm.playlistId,
                  true
                );

              if (result) {
                setPlaylist({
                  id: result.id || '',
                  name: result.name,
                  picture: result.imageUrl,
                });
              } else {
                // Error
                throw new Error('Empty playlist.');
              }
            } catch (error) {
              // Log
              console.debug('🔴 Could not get the playlist.', error);
            }
          }
        }

        // Set models
        setModel(officeContext.state.playlistItemForm);
        setOriginalModel(officeContext.state.playlistItemForm);
      }
    };

    // Init
    init();

    // Clean up
    return () => {
      // Log
      console.debug('🧹 Playlist item form button states cleaned in office context.');

      // Clear button states
      officeContext.clearPlaylistItemFormSubmitButtonState();
      officeContext.clearPlaylistItemFormDeleteButtonState();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeContext.state.playlistItemForm]);

  // Form validation
  useEffect(() => {
    if (model && originalModel) {
      if (caregiverMode === CaregiverMode.SystemAdministrator) {
        // ----- Fields validation -----
        // Holds validation
        let titleValid: boolean = false;
        let playlistValid: boolean = false;
        let resourceUrlValid: boolean = false;

        // Title validation
        if (model.title && model.title.trim().length > 0) {
          // No errors
          if (titleError !== undefined) {
            setTitleError(undefined);
          }

          // Valid
          titleValid = true;
        } else {
          // Errors
          if (showTitleError) {
            setTitleError(
              model.id
                ? t(translations.playlist_item_form.errors.edit_form_validation_invalid_field)
                : t(translations.playlist_item_form.errors.create_form_validation_invalid_field)
            );
          }

          // Invalid
          titleValid = false;
        }

        // Playlist validation
        if (model.playlistId && model.playlistId.trim().length > 0) {
          // No errors
          if (playlistError !== undefined) {
            setPlaylistError(undefined);
          }

          // Valid
          playlistValid = true;
        } else {
          // Errors
          if (showPlaylistError) {
            setPlaylistError(
              model.id
                ? t(translations.playlist_item_form.errors.edit_form_validation_invalid_field)
                : t(translations.playlist_item_form.errors.create_form_validation_invalid_field)
            );
          }

          // Invalid
          playlistValid = false;
        }

        // Resource URL validation
        if (model.resourceUrl && model.resourceUrl.trim().length > 0) {
          if (URL_REGEX.test(model.resourceUrl)) {
            // No errors
            if (resourceUrlError !== undefined) {
              setResourceUrlError(undefined);
            }

            // Valid
            resourceUrlValid = true;
          } else {
            // Errors
            if (showResourceUrlError) {
              setResourceUrlError(
                t(translations.playlist_item_form.errors.form_validation_invalid_resource_url)
              );
            }

            // Invalid
            resourceUrlValid = false;
          }
        } else {
          // Errors
          if (showResourceUrlError) {
            setResourceUrlError(
              model.id
                ? t(translations.playlist_item_form.errors.edit_form_validation_invalid_field)
                : t(translations.playlist_item_form.errors.create_form_validation_invalid_field)
            );
          }

          // Invalid
          resourceUrlValid = false;
        }
        // ----- Fields validation -----

        // ----- Data changed -----
        let dataWasUpdated: boolean = false;

        if (
          model.title.trim() !== originalModel.title.trim() ||
          (model?.description || '').trim() !== (originalModel?.description || '').trim() ||
          model.playlistId.trim() !== originalModel.playlistId.trim() ||
          model.resourceUrl.trim() !== originalModel.resourceUrl.trim() ||
          (model.titleLocalized?.enUS || '').trim() !==
            (originalModel.titleLocalized?.enUS || '').trim() ||
          (model.titleLocalized?.esES || '').trim() !==
            (originalModel.titleLocalized?.esES || '').trim() ||
          (model.titleLocalized?.ptPT || '').trim() !==
            (originalModel.titleLocalized?.ptPT || '').trim() ||
          (model.titleLocalized?.ptBR || '').trim() !==
            (originalModel.titleLocalized?.ptBR || '').trim() ||
          (model.descriptionLocalized?.enUS || '').trim() !==
            (originalModel.descriptionLocalized?.enUS || '').trim() ||
          (model.descriptionLocalized?.esES || '').trim() !==
            (originalModel.descriptionLocalized?.esES || '').trim() ||
          (model.descriptionLocalized?.ptPT || '').trim() !==
            (originalModel.descriptionLocalized?.ptPT || '').trim() ||
          (model.descriptionLocalized?.ptBR || '').trim() !==
            (originalModel.descriptionLocalized?.ptBR || '').trim() ||
          model.isActive !== originalModel.isActive ||
          model.thumbnailUrl !== originalModel.thumbnailUrl ||
          (model.regionalLockout || []).join() !== (originalModel.regionalLockout || []).join()
        ) {
          // Set it
          setDataChanged(true);

          // Set
          dataWasUpdated = true;
        } else {
          // Set it
          setDataChanged(false);

          // Set
          dataWasUpdated = false;
        }
        // ----- Data changed -----

        // ----- Submit button validation -----
        if (model.id) {
          // ----> Edit <----
          if (titleValid && resourceUrlValid && playlistValid && dataWasUpdated) {
            // Enable submit button
            setSubmitButtonDisabled(false);
          } else {
            // Disable submit button
            setSubmitButtonDisabled(true);
          }
        } else {
          // ----> Create <----
          if (titleValid && resourceUrlValid && playlistValid) {
            // Enable submit button
            setSubmitButtonDisabled(false);
          } else {
            // Disable submit button
            setSubmitButtonDisabled(true);
          }
        }
        // ----- Submit button validation -----
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    model,
    originalModel,
    playlistError,
    caregiverMode,
    resourceUrlError,
    showPlaylistError,
    showResourceUrlError,
    showTitleError,
    titleError,
  ]);

  // Handle change text fields
  const handleChangeTextField = (
    field: 'title' | 'description' | 'resourceUrl',
    value: string | undefined | null
  ) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set it
    setModel({
      ...model,
      [field]: finalValue,
    } as PlaylistItem);
  };

  // Close the cropper dialog
  const handleThumbnailImageFileCropperDialogClosed = (result?: File) => {
    // Clean values
    setThumbnailImageCropperFile(undefined);

    // If a file exists, process it
    if (result) {
      setThumbnailImageFile(result);

      // Initialize a new file reader
      const reader = new FileReader();

      // On reader load
      reader.onload = () => {
        // Set values
        setModel({
          ...model,
          thumbnailUrl: reader.result as string, // base64
        } as PlaylistItem);
      };

      // Launch reading process
      reader.readAsDataURL(result);
    }
  };
  // Fetch playlists
  const handleFetchPlaylists = async (
    limit: number,
    skip: number,
    search?: string
  ): Promise<{data: Array<SearchSelectOption<SearchSelectInputItem>>; total: number}> => {
    // Get playlists
    const response = await PlaylistsRepository.instance.getPlaylistsAsSystemAdministrator(
      skip,
      limit,
      undefined,
      search,
      true
    );

    // Return
    return {
      data: response.data.map((obj) => {
        return {
          id: obj.id || '',
          name: obj.name || '',
          item: {id: obj.id || '', name: obj.name || '', picture: obj.imageUrl},
          avatar: obj.imageUrl,
        };
      }),
      total: response.totalCount,
    };
  };

  // Delete playlist item
  const handleDeletePlaylistItem = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || caregiverMode !== CaregiverMode.SystemAdministrator) {
      return;
    }

    try {
      // Disable submit button
      setSubmitButtonDisabled(true);

      // Update delete button state
      officeContext.setPlaylistItemFormDeleteButtonState(SubmitButtonState.Loading);

      // Delete the playlist item
      if (originalModel.id) {
        await PlaylistsRepository.instance.deletePlaylistItemById(
          originalModel.playlistId,
          originalModel.id
        );
      } else {
        // Error
        throw new Error('Invalid values to delete the playlist item.');
      }

      // Wait some time just to make sure the button state was updated
      // so the router prompt won't appear when the history is updated
      setTimeout(() => {
        setIsDeleting(false);
        setDeleteDialogOpened(false);

        // Navigate back
        history.replace(
          props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
            ? `${props.redirectUrl}playlists/${originalModel.playlistId}/items?${ScreenPaths.PlaylistItemDeletedQuery}=${originalModel.title}`
            : `${props.redirectUrl}/playlists/${originalModel.playlistId}/items?${ScreenPaths.PlaylistItemDeletedQuery}=${originalModel.title}`
        );
      }, 500);
    } catch (error: any) {
      // Log
      console.debug('🔴 Could not delete the playlist item.', error);

      // Update delete button state
      officeContext.setPlaylistItemFormDeleteButtonState(SubmitButtonState.Error);
      setIsDeleting(false);
      setDeleteDialogOpened(false);
      // Enable submit button again
      setSubmitButtonDisabled(false);
    }
  };

  // Submit changes
  const handleSubmit = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || caregiverMode !== CaregiverMode.SystemAdministrator) {
      return;
    }

    try {
      // Update submit button state
      officeContext.setPlaylistItemFormSubmitButtonState(SubmitButtonState.Loading);

      // Disabled delete button
      setDeleteButtonDisabled(true);

      if (model.id && caregiverMode === CaregiverMode.SystemAdministrator) {
        // ---> Edit <---

        // Playlist thumbnail image to upload
        let uploadedThumbnailImageLinkingUrl: string | null;
        if (thumbnailImageFile) {
          // Request body
          const requestBodyThumbnailImageFile: PostStorageRequest<PlaylistItemThumbnailStorageRequestData> =
            {
              type: StorageType.PlaylistItemThumbnail,
              fileType: StorageFileType.JPG,
              data: {
                playlistId: model.playlistId,
                playlistItemId: model.id,
              },
            };

          // Some file was selected to be upload, request upload to the backend
          const storageThumbnailImageRequestResult: StorageRequestResult<PlaylistItemThumbnailStorageRequestData> =
            await StorageRequestRepository.instance.create<PlaylistItemThumbnailStorageRequestData>(
              requestBodyThumbnailImageFile
            );

          // Upload file
          uploadedThumbnailImageLinkingUrl =
            await StorageRequestRepository.instance.upload<PlaylistItemThumbnailStorageRequestData>(
              storageThumbnailImageRequestResult,
              thumbnailImageFile
            );
        } else {
          uploadedThumbnailImageLinkingUrl = model.thumbnailUrl || null;
        }

        // Request body
        const requestBody: PutPlaylistItem = {
          isActive: model.isActive,
          title: model.title.trim(),
          resourceUrl: model.resourceUrl.trim(),
          playlistId: model.playlistId,
          thumbnailUrl: uploadedThumbnailImageLinkingUrl,
          description: model.description?.trim() || null,
          regionalLockout: model.regionalLockout || null,
          titleLocalized: model.titleLocalized
            ? nullifyLocalizedString(model.titleLocalized)
            : null,
          descriptionLocalized: model.descriptionLocalized
            ? nullifyLocalizedString(model.descriptionLocalized)
            : null,
        };

        // Update playlist item
        const updatedPlaylistItem = await PlaylistsRepository.instance.updatePlaylistItemById(
          originalModel.playlistId,
          model.id,
          requestBody
        );

        // Wait some time just to make sure the button state was updated
        // so the router prompt won't appear when the history is updated
        setTimeout(() => {
          // Reset navigation to edit mode
          history.replace(
            props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
              ? `${props.redirectUrl}playlists/${updatedPlaylistItem.playlistId}/items/${updatedPlaylistItem.id}`
              : `${props.redirectUrl}/playlists/${updatedPlaylistItem.playlistId}/items/${updatedPlaylistItem.id}`
          );

          // Reset context
          officeContext.setPlaylistItemForm(updatedPlaylistItem);

          // Show toast
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.playlist_item_form.success.edit_success_message),
          });

          // Update submit button state
          officeContext.setPlaylistItemFormSubmitButtonState(SubmitButtonState.Success);

          // Enable delete button
          setDeleteButtonDisabled(false);
        }, 500);
      } else if (!model.id && caregiverMode === CaregiverMode.SystemAdministrator) {
        // ---> Create <---

        // Request body
        // Note: We will set the thumbnailUrl after the playlist is created.
        const requestBody: PostPlaylistItem = {
          isActive: model.isActive,
          title: model.title.trim(),
          resourceUrl: model.resourceUrl.trim(),
          description: model.description?.trim() || undefined,
          regionalLockout: model.regionalLockout || undefined,
          titleLocalized: model.titleLocalized
            ? sanitizeLocalizedString(model.titleLocalized)
            : undefined,
          descriptionLocalized: model.descriptionLocalized
            ? sanitizeLocalizedString(model.descriptionLocalized)
            : undefined,
        };

        // Create the playlist item
        const newPlaylistItem = await PlaylistsRepository.instance.createPlaylistItem(
          model.playlistId,
          requestBody
        );

        // Result
        let resultPlaylistItem: PlaylistItem = {...newPlaylistItem};

        // Playlist thumbnail image to upload
        let uploadedThumbnailImageLinkingUrl: string | undefined;
        if (thumbnailImageFile) {
          // If an error occurs, we can't fail the whole creation process because the playlist item was previously created
          try {
            // Request body
            const requestBodyThumbnailImageFile: PostStorageRequest<PlaylistItemThumbnailStorageRequestData> =
              {
                type: StorageType.PlaylistItemThumbnail,
                fileType: StorageFileType.JPG,
                data: {
                  playlistId: newPlaylistItem.playlistId,
                  playlistItemId: newPlaylistItem.id || '',
                },
              };

            // Some file was selected to be upload, request upload to the backend
            const storageThumbnailImageRequestResult: StorageRequestResult<PlaylistItemThumbnailStorageRequestData> =
              await StorageRequestRepository.instance.create<PlaylistItemThumbnailStorageRequestData>(
                requestBodyThumbnailImageFile
              );

            // Upload file
            uploadedThumbnailImageLinkingUrl =
              await StorageRequestRepository.instance.upload<PlaylistItemThumbnailStorageRequestData>(
                storageThumbnailImageRequestResult,
                thumbnailImageFile
              );
          } catch (error) {
            // Error
            console.debug('🔴 Could not process the playlist thumbnail image.', error);
          }
        }

        // Update the playlist item if necessary
        if (uploadedThumbnailImageLinkingUrl) {
          // If an error occurs, we can't fail the whole creation process because the playlist item was previously created
          try {
            // Request body
            // Ignore all values except the required ones and the ones we want to update
            const secondRequestBody: PutPlaylistItem = {
              title: newPlaylistItem.title,
              playlistId: newPlaylistItem.playlistId,
              isActive: newPlaylistItem.isActive,
              resourceUrl: newPlaylistItem.resourceUrl,
              thumbnailUrl: uploadedThumbnailImageLinkingUrl,
            };

            // Edit playlist item
            const updatedPlaylistItem = await PlaylistsRepository.instance.updatePlaylistItemById(
              newPlaylistItem.playlistId,
              newPlaylistItem.id || '',
              secondRequestBody
            );

            // Update the result
            resultPlaylistItem = {...updatedPlaylistItem};
          } catch (error) {
            // Error
            console.debug('🔴 Could not update the playlist item after creating it.', error);
          }
        }

        // Wait some time just to make sure the button state was updated
        // so the router prompt won't appear when the history is updated
        setTimeout(() => {
          // Reset navigation to edit mode
          history.replace(
            props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
              ? `${props.redirectUrl}playlists/${model.playlistId}/items/${newPlaylistItem.id}`
              : `${props.redirectUrl}/playlists/${model.playlistId}/items/${newPlaylistItem.id}`
          );

          // Reset context
          officeContext.setPlaylistItemForm(resultPlaylistItem);

          // Show toast
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.playlist_item_form.success.create_success_message, {
              name: resultPlaylistItem.title,
            }),
          });

          // Update submit button state
          officeContext.setPlaylistItemFormSubmitButtonState(SubmitButtonState.Success);

          // Enable delete button
          setDeleteButtonDisabled(false);
        }, 500);
      } else {
        // Throw error
        throw new Error('Missing permissions.');
      }
    } catch (error) {
      // Log
      console.debug('🔴 Could not edit/create the playlist item.', error);

      // Update submit button state
      officeContext.setPlaylistItemFormSubmitButtonState(SubmitButtonState.Error);

      // Enable delete button
      setDeleteButtonDisabled(false);
    }
  };

  // Get title and description
  let titleLocalizedReadOnly: string = '';
  let descriptionLocalizedReadOnly: string = '';
  switch (i18n.language) {
    case AppLanguageTag.ptPT:
      titleLocalizedReadOnly = model?.titleLocalized?.ptPT || model?.title || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.ptPT || model?.description || '';
      break;
    case AppLanguageTag.esES:
      titleLocalizedReadOnly = model?.titleLocalized?.esES || model?.title || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.esES || model?.description || '';
      break;
    case AppLanguageTag.glES:
      titleLocalizedReadOnly = model?.titleLocalized?.glES || model?.title || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.glES || model?.description || '';
      break;
    case AppLanguageTag.enUS:
      titleLocalizedReadOnly = model?.titleLocalized?.enUS || model?.title || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.enUS || model?.description || '';
      break;
    case AppLanguageTag.ptBR:
      titleLocalizedReadOnly = model?.titleLocalized?.ptBR || model?.title || '';
      descriptionLocalizedReadOnly = model?.descriptionLocalized?.ptBR || model?.description || '';
      break;
    default:
      titleLocalizedReadOnly = model?.title || '';
      descriptionLocalizedReadOnly = model?.description || '';
      break;
  }

  // Strings to use in the form
  let routerDialogTitle: string = '';
  let routerDialogContent: string = '';
  let routerCancelToast: string = '';
  switch (caregiverMode) {
    case CaregiverMode.SystemAdministrator:
      routerDialogTitle = model?.id
        ? t(translations.playlist_item_form.router_prompt_edit_title, {
            name: originalModel?.title || '',
          })
        : t(translations.playlist_item_form.router_prompt_create_title, {name: model?.title || ''});
      routerDialogContent = model?.id
        ? t(translations.playlist_item_form.router_prompt_edit_message)
        : t(translations.playlist_item_form.router_prompt_create_message);
      routerCancelToast = model?.id
        ? t(translations.playlist_item_form.edit_cancel_toast, {
            name: originalModel?.title || '',
          })
        : t(translations.playlist_item_form.create_cancel_toast);
      break;
    default:
      break;
  }

  return (
    <ContentPage>
      {model ? (
        <form>
          <Box className="playlist-item-form-container">
            {canWrite && (
              <LocalizedStringInput
                defaultValueLabel={t(translations.playlist_item_form.default_title_input)}
                defaultValue={model.title || ''}
                localizedValuesLabel={t(translations.playlist_item_form.title_translations_input)}
                localizedValues={model.titleLocalized}
                handleChangeDefaultValue={(value) => handleChangeTextField('title', value)}
                handleChangeLocalizedValues={(values: LocalizedString) =>
                  setModel({
                    ...model,
                    titleLocalized: values,
                  } as PlaylistItem)
                }
                handleBlurDefaultValue={() => {
                  if (!showTitleError) {
                    setShowTitleError(true);
                  }
                }}
                defaultValueError={titleError}
                defaultValueRequired={true}
                disabled={
                  officeContext.state.playlistItemFormSubmitButtonState ===
                    SubmitButtonState.Loading ||
                  officeContext.state.playlistItemFormDeleteButtonState ===
                    SubmitButtonState.Loading
                }
              />
            )}
            {!canWrite && (
              <ReadOnlyTextField
                className="playlist-item-form-line-1"
                label={t(translations.playlist_item_form.default_title_input)}
                value={titleLocalizedReadOnly}
                disabledTooltip={t(translations.shared.input_disabled_tooltip)}
              />
            )}
            {canWrite && (
              <Box className="playlist-item-form-line-2">
                <FlatSearchSelectInput<SearchSelectInputItem>
                  className="playlist-item-form-line-2-left"
                  label={t(translations.playlist_item_form.playlist_input)}
                  value={
                    playlist
                      ? {
                          id: playlist.id,
                          name: playlist.name,
                          item: playlist,
                          avatar: playlist.picture,
                        }
                      : undefined
                  }
                  handleChange={(value?: SearchSelectOption<SearchSelectInputItem>) => {
                    setModel({
                      ...model,
                      playlistId: value?.id,
                    } as PlaylistItem);
                    setPlaylist(value?.item || undefined);
                  }}
                  handleFetchData={handleFetchPlaylists}
                  handleBlur={() => {
                    if (!showPlaylistError) {
                      setShowPlaylistError(true);
                    }
                  }}
                  fetchWhenEmpty={true}
                  disabled={
                    officeContext.state.playlistItemFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.playlistItemFormDeleteButtonState ===
                      SubmitButtonState.Loading
                  }
                  error={playlistError}
                  required={true}
                  disableClear={true}
                  avatar={{isIcon: false, type: 'other'}}
                />
                <SelectInput
                  id="playlist-item-form-state-select"
                  className="playlist-item-form-line-2-right"
                  label={t(translations.playlist_item_form.state_input)}
                  value={model.isActive.toString()}
                  handleChange={(value) =>
                    setModel({
                      ...model,
                      isActive: JSON.parse(value),
                    })
                  }
                  required={true}
                  disabled={
                    officeContext.state.playlistItemFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.playlistItemFormDeleteButtonState ===
                      SubmitButtonState.Loading
                  }
                  items={[
                    {value: 'true', label: t(translations.playlist_item_form.state_value_true)},
                    {value: 'false', label: t(translations.playlist_item_form.state_value_false)},
                  ]}
                />
              </Box>
            )}
            {!canWrite && (
              <Box className="playlist-item-form-line-2">
                <ReadOnlyTextField
                  className="playlist-item-form-line-2-left"
                  label={t(translations.playlist_item_form.playlist_input)}
                  value={playlist?.name || ''}
                  startAdornment={
                    playlist ? (
                      <Avatar
                        className="playlist-item-form-playlist-input-start-adornment"
                        src={playlist.picture}
                        alt={playlist.name}
                        sx={{
                          width: avatarSizesAndCoefficients.avatarSizes.tiny,
                          height: avatarSizesAndCoefficients.avatarSizes.tiny,
                          borderRadius: `${
                            avatarSizesAndCoefficients.avatarSizes.tiny *
                            avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersHigh
                          }px`,
                          borderColor: theme.palette.action.disabled,
                        }}
                      >
                        <></>
                      </Avatar>
                    ) : undefined
                  }
                  disabledTooltip={t(translations.shared.input_disabled_tooltip)}
                  disabled
                />
                <ReadOnlyTextField
                  className="playlist-item-form-line-2-right"
                  label={t(translations.playlist_item_form.state_input)}
                  value={
                    model.isActive === true
                      ? t(translations.playlist_item_form.state_value_true)
                      : t(translations.playlist_item_form.state_value_false)
                  }
                  disabledTooltip={t(translations.shared.input_disabled_tooltip)}
                  disabled
                />
              </Box>
            )}
            {canWrite && (
              <LocalizedStringInput
                className="playlist-item-form-line-3"
                defaultValueLabel={t(translations.playlist_item_form.default_description_input)}
                defaultValue={model.description || ''}
                localizedValuesLabel={t(
                  translations.playlist_item_form.description_translations_input
                )}
                localizedValues={model.descriptionLocalized}
                handleChangeDefaultValue={(value) => handleChangeTextField('description', value)}
                handleChangeLocalizedValues={(values: LocalizedString) =>
                  setModel({
                    ...model,
                    descriptionLocalized: values,
                  } as PlaylistItem)
                }
                defaultValueRequired={false}
                disabled={
                  officeContext.state.playlistItemFormSubmitButtonState ===
                    SubmitButtonState.Loading ||
                  officeContext.state.playlistItemFormDeleteButtonState ===
                    SubmitButtonState.Loading
                }
                multiline={true}
              />
            )}
            {!canWrite && (
              <ReadOnlyTextField
                className="playlist-item-form-line-3"
                label={t(translations.playlist_item_form.default_description_input)}
                value={descriptionLocalizedReadOnly}
                disabledTooltip={t(translations.shared.input_disabled_tooltip)}
                disabled
              />
            )}
            {canWrite && (
              <Box className="playlist-item-form-line-4">
                <MultipleSelectInput
                  id="playlist-item-form-region-lockout-multiple-select"
                  label={t(translations.playlist_item_form.regional_lockout_input)}
                  placeholder={t(translations.playlist_item_form.regional_lockout_placeholder)}
                  value={model.regionalLockout}
                  clearable={true}
                  disabled={
                    officeContext.state.playlistItemFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.playlistItemFormDeleteButtonState ===
                      SubmitButtonState.Loading
                  }
                  handleChange={(value?: Array<string>) =>
                    setModel({
                      ...model,
                      regionalLockout: value,
                    } as PlaylistItem)
                  }
                  items={regions.map((obj) => {
                    return {label: t(obj.translation), value: obj.type};
                  })}
                />
              </Box>
            )}
            {!canWrite && (
              <ReadOnlyTextField
                className="playlist-item-form-line-4"
                label={t(translations.playlist_item_form.regional_lockout_input)}
                value={
                  model.regionalLockout && model.regionalLockout.length > 0
                    ? model.regionalLockout
                        .map((region) => t(translations.regions[region]))
                        .join(', ')
                    : ''
                }
                disabledTooltip={t(translations.shared.input_disabled_tooltip)}
                disabled
              />
            )}
            {canWrite && (
              <TextInput
                className="playlist-item-form-line-5"
                label={t(translations.playlist_item_form.resource_url_input)}
                value={model.resourceUrl || ''}
                handleChange={(value) => handleChangeTextField('resourceUrl', value)}
                handleBlur={() => {
                  if (!showResourceUrlError) {
                    setShowResourceUrlError(true);
                  }
                }}
                disabled={
                  officeContext.state.playlistItemFormSubmitButtonState ===
                    SubmitButtonState.Loading ||
                  officeContext.state.playlistItemFormDeleteButtonState ===
                    SubmitButtonState.Loading
                }
                required={true}
                error={resourceUrlError}
                type="url"
              />
            )}
            {!canWrite && (
              <Box className="playlist-item-form-line-5">
                <FormLabel className="playlist-item-form-label" component="legend">
                  {t(translations.playlist_item_form.resource_input)}
                </FormLabel>
                <EmbedYoutubeVideo url={model.resourceUrl || ''} title={titleLocalizedReadOnly} />
              </Box>
            )}
            {canWrite && (
              <Box className="playlist-item-form-line-6">
                <FormLabel
                  className="playlist-item-form-label"
                  component="legend"
                  disabled={
                    officeContext.state.playlistItemFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.playlistItemFormDeleteButtonState ===
                      SubmitButtonState.Loading
                  }
                >
                  {t(translations.playlist_item_form.thumbnail_image_url_input)}
                </FormLabel>
                <SmoothImage
                  src={model.thumbnailUrl || ''}
                  width={appSizes.profileImageSize.width}
                  height={appSizes.profileImageSize.height}
                  imageWidth={appSizes.profileImageSize.width}
                  imageHeight={appSizes.profileImageSize.height}
                  borderRadius={appSizes.appButtonSize.borderRadius}
                />
                <Box
                  className="playlist-item-form-image-alert"
                  sx={{
                    maxWidth: `${appSizes.profileImageSize.width}px`,
                    width: '100%',
                  }}
                >
                  <Alert severity="info">
                    {t(translations.playlist_item_form.thumbnail_image_info_message)}
                  </Alert>
                </Box>
                <Box
                  className="playlist-item-form-image-edit-button"
                  sx={{
                    maxWidth: `${appSizes.profileImageSize.width}px`,
                    width: '100%',
                  }}
                >
                  {model.thumbnailUrl && (
                    <Fab
                      // Override with error color because it's not in the color prop
                      sx={{
                        backgroundColor: theme.palette.error.main,
                        '&:hover': {
                          backgroundColor: theme.palette.error.dark,
                        },
                        marginRight: '8px',
                      }}
                      disabled={
                        officeContext.state.playlistItemFormSubmitButtonState ===
                          SubmitButtonState.Loading ||
                        officeContext.state.playlistItemFormDeleteButtonState ===
                          SubmitButtonState.Loading
                      }
                      size="small"
                      color="primary"
                      onClick={() => {
                        setModel({
                          ...model,
                          thumbnailUrl: undefined,
                        } as PlaylistItem);
                        setThumbnailImageFile(undefined);
                      }}
                    >
                      <DeleteIcon />
                    </Fab>
                  )}
                  <input
                    ref={thumbnailImageInputFileRef}
                    disabled={
                      officeContext.state.playlistItemFormSubmitButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.playlistItemFormDeleteButtonState ===
                        SubmitButtonState.Loading
                    }
                    style={{display: 'none'}}
                    accept="image/jpeg"
                    type="file"
                    value=""
                    onChange={(event: any) => {
                      if (event.target.files[0]) {
                        setThumbnailImageCropperFile(event.target.files[0]);
                      }
                    }}
                  />
                  <Fab
                    disabled={
                      officeContext.state.playlistItemFormSubmitButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.playlistItemFormDeleteButtonState ===
                        SubmitButtonState.Loading
                    }
                    size="small"
                    color="primary"
                    onClick={() => thumbnailImageInputFileRef.current.click()}
                  >
                    <EditIcon />
                  </Fab>
                </Box>
              </Box>
            )}
            {!canWrite && (
              <Box className="playlist-item-form-line-6">
                <FormLabel className="playlist-item-form-label" component="legend">
                  {t(translations.playlist_item_form.thumbnail_image_url_input)}
                </FormLabel>
                <SmoothImage
                  src={model.thumbnailUrl || ''}
                  width={appSizes.profileImageSize.width}
                  height={appSizes.profileImageSize.height}
                  imageWidth={appSizes.profileImageSize.width}
                  imageHeight={appSizes.profileImageSize.height}
                  borderRadius={appSizes.appButtonSize.borderRadius}
                />
              </Box>
            )}
            {canWrite && thumbnailImageCropperFile && (
              <ImageCropperDialog
                open={true}
                imageFile={thumbnailImageCropperFile}
                handleClose={handleThumbnailImageFileCropperDialogClosed}
                aspect={appSizes.profileImageSize.width / appSizes.profileImageSize.height}
                size={`${appSizes.profileImageSize.width}x${appSizes.profileImageSize.height}`}
              />
            )}
          </Box>
          {props.showExitButton && caregiverMode === CaregiverMode.Professional && !canWrite && (
            <Stack
              flexDirection="row"
              justifyContent="flex-start"
              sx={{width: '100%', maxWidth: '805px', margin: '67px auto 0 auto'}}
            >
              <Button
                variant="text"
                color="error"
                sx={{
                  width: '100%',
                  height: '40px',
                  padding: '8px 16px',
                  borderRadius: '5px',
                  maxWidth: '252px',
                }}
                onClick={() => history.push(props.redirectUrl)}
              >
                {t(translations.user_form.cancel_button)}
              </Button>
            </Stack>
          )}
        </form>
      ) : (
        <LoadingContent />
      )}
      {/* Delete button and dialog */}
      {caregiverMode === CaregiverMode.SystemAdministrator && (
        <>
          <Box className="playlist-item-form-delete-container">
            <Divider />
            <Box className="playlist-item-form-delete-button-container">
              {model && model.id && (
                <SubmitButton
                  disabled={deleteButtonDisabled}
                  state={
                    officeContext.state.playlistItemFormDeleteButtonState ||
                    SubmitButtonState.Normal
                  }
                  onStateChange={(newState: SubmitButtonState) =>
                    officeContext.setPlaylistItemFormDeleteButtonState(newState)
                  }
                  normalStateLabel={t(translations.playlist_item_form.delete_button)}
                  loadingStateLabel={t(translations.playlist_item_form.deleting_button)}
                  successStateLabel={t(
                    translations.playlist_item_form.success.delete_success_button
                  )}
                  errorStateLabel={t(translations.playlist_item_form.errors.error_on_delete_button)}
                  endIcon={<DeleteIcon />}
                  type="button"
                  variant="outlined"
                  color="error"
                  onClick={(event: any) => {
                    event.preventDefault();
                    setDeleteDialogOpened(true);
                  }}
                />
              )}
              <SubmitButton
                disabled={submitButtonDisabled}
                state={
                  officeContext.state.playlistItemFormSubmitButtonState || SubmitButtonState.Normal
                }
                onStateChange={(newState: SubmitButtonState) =>
                  officeContext.setPlaylistItemFormSubmitButtonState(newState)
                }
                normalStateLabel={t(translations.playlist_item_form.save_button)}
                loadingStateLabel={t(translations.playlist_item_form.saving_button)}
                successStateLabel={t(translations.playlist_item_form.success.save_success_button)}
                errorStateLabel={t(translations.playlist_item_form.errors.error_on_save_button)}
                endIcon={<SaveIcon />}
                onClick={handleSubmit}
                sx={{
                  width: '100%',
                  maxWidth: '252px',
                }}
              />
            </Box>
          </Box>
          <Modal
            open={deleteDialogOpened}
            maxWidth="sm"
            fullWidth
            className="associate-device-form-modal"
            preventCloseClick
            onClose={() => setDeleteDialogOpened(false)}
            actions={[
              {
                label: t(translations.modal.cancel),
                type: 'destructive',
                onClick: () => setDeleteDialogOpened(false),
                disabled: isDeleting,
              },
              {
                label: t(translations.modal.confirm),
                type: 'nondestructive',
                onClick: handleDeletePlaylistItem,
                loading: isDeleting,
                disabled: isDeleting,
              },
            ]}
          >
            <Typography className="title">
              {t(translations.playlist_item_form.delete_dialog_title, {
                name: originalModel?.title || '',
              })}
            </Typography>
            <Typography className="subtitle">
              {t(translations.playlist_item_form.delete_dialog_content)}
            </Typography>
          </Modal>
        </>
      )}
      {/* Router */}
      {model && caregiverMode === CaregiverMode.SystemAdministrator && (
        <RouterPrompt
          // Show prompt if the data changed but make sure the path
          // is not changing because the playlist item was deleted/created
          when={
            dataChanged &&
            officeContext.state.playlistItemFormDeleteButtonState !== SubmitButtonState.Loading &&
            officeContext.state.playlistItemFormSubmitButtonState !== SubmitButtonState.Loading
          }
          subtitle={routerDialogTitle}
          message={routerDialogContent}
          onOK={() => true}
          onCancel={() => false}
          toastNotification={{
            severity: 'info',
            message: routerCancelToast,
          }}
        />
      )}
    </ContentPage>
  );
};
